import React, { useState } from "react";
import { api, toastError, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interactionId: string;
  version: number;
  onSuccess: () => void;
};

function makeIdempotencyKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "ik-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function InteractionVoidDialog({
  open, onOpenChange, interactionId, version, onSuccess,
}: Props) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    setError(null);
    if (!reason.trim()) {
      setError("Please provide a reason for voiding this Interaction.");
      return;
    }
    if (reason.length > 500) {
      setError("Reason must be 500 characters or fewer.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(
        `/crm/interactions/${interactionId}/void`,
        { if_version: version, reason: reason.trim() },
        { headers: { "Idempotency-Key": makeIdempotencyKey() } },
      );
      onSuccess();
      onOpenChange(false);
      setReason("");
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        setError("This Interaction was updated by someone else. Refresh and try again.");
      } else if (status === 403) {
        setError("You do not have permission to void this Interaction.");
      } else {
        setError(formatApiError(e));
      }
      toastError(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="interaction-void-dialog">
        <DialogHeader>
          <DialogTitle>Void this Interaction</DialogTitle>
          <DialogDescription>
            Voiding is permanent — the record stays visible for audit but no longer counts toward
            performance metrics.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Enter a reason so the timeline explains what changed.
        </p>
        <div className="mt-4">
          <Label htmlFor="void-reason" className="text-xs">Reason (required)</Label>
          <Textarea
            id="void-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. wrong client selected, logged twice…"
            data-testid="interaction-void-reason"
          />
          {error && (
            <p className="text-xs text-rose-700 mt-2" data-testid="interaction-void-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="interaction-void-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="interaction-void-submit">
            {submitting ? "Voiding…" : "Void Interaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
